import { useEffect, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useFlags } from '@mint-lib/routing-context';
import { SlotPortal } from '@mint-lib/slots';

import { useUserRole } from '../../api/queries/userRoles.js';
import {
  CreateRoleType,
  fetchRolesMeta,
  RolesMetaType,
  RoleType,
  type EditRoleType,
} from '../../api/roles/index.js';
import type { WithPaginationResponse } from '../../api/types.js';
import { useModal } from '../../context/ModalContext/useModal.js';
import Filters from '../../features/Filters/Filters.jsx';
import RoleTable from '../../features/RoleTable/index.js';
import useIsReadOnlyPermission from '../../hooks/useIsReadOnlyPermission/useIsReadOnlyPermission.js';
import useTablePagination from '../../hooks/useTablePagination/useTablePagination.jsx';
import { CreateRoleModal } from '../../organisms/RoleModal/RoleOperationModals.jsx';
import { DEFAULT_PAGINATION_STATE } from '../../store/atoms/TablePagination/constants.js';

const RolePage = () => {
  const ff = useFlags('@mint/arm', ['aiHubPermission', 'mint2Permissions']);
  const { t } = useTranslation('@myn/permissions');

  const isReadonlyPermission = useIsReadOnlyPermission();

  const { open: isNewModalOpened, setOpen: setIsNewModalOpened } = useModal();

  const [rolesMeta, setRolesMeta] = useState<RolesMetaType | null>(null);

  const { userRole, createRole, editRole, refetchRoles } = useUserRole();

  // init pagination
  useTablePagination({
    ...DEFAULT_PAGINATION_STATE,
    totalItems:
      (userRole?.data as WithPaginationResponse<RoleType>)?.count || 0,
  });

  useEffect(() => {
    fetchRolesMeta(
      ff.aiHubPermission.enabled,
      ff.mint2Permissions.enabled,
    ).then(setRolesMeta);
  }, [ff.aiHubPermission.enabled]);

  const handleCreate = (options: CreateRoleType) =>
    createRole.mutateAsync(options, {
      onSuccess: (data) => {
        enqueueSnackbar({
          removable: true,
          variant: 'success',
          title: t('Success'),
          textLayout: 'multiline',
          subTitle: t('Role {{dataName}} was created', { dataName: data.name }),
        });

        refetchRoles();
      },
      onError: () => {
        enqueueSnackbar({
          removable: true,
          variant: 'error',
          title: t('Error'),
          textLayout: 'multiline',
          subTitle: t(
            'An error occurred while creating the role. Please try again',
          ),
        });
      },
    });

  const handleUpdate = (options: Partial<EditRoleType>) =>
    editRole.mutateAsync(options, {
      onSuccess: (data) => {
        enqueueSnackbar({
          removable: true,
          variant: 'success',
          title: t('Success'),
          textLayout: 'multiline',
          subTitle: t('Role {{dataName}} was updated', {
            dataName: data.name,
          }),
        });

        refetchRoles();
      },
      onError: () => {
        enqueueSnackbar({
          removable: true,
          variant: 'error',
          title: t('Error'),
          textLayout: 'multiline',
          subTitle: t(
            'An error occurred while creating the role. Please try again',
          ),
        });
      },
    });

  const noResultsStatus =
    !userRole.isLoading && userRole.data?.results?.length === 0;

  const notNullRolesData = (userRole.data?.results ?? []) as RoleType[];

  return (
    <>
      <RoleTable
        rolesMeta={rolesMeta}
        data={notNullRolesData}
        dataIsLoading={userRole.isPlaceholderData || userRole.isLoading}
        isReadonlyPermission={isReadonlyPermission}
        noResultsStatus={noResultsStatus}
      />
      <SlotPortal name="filters">
        <Filters key={'role'} />
      </SlotPortal>

      {/* CreateRoleModal closes itself after a successful save. */}
      {isNewModalOpened && rolesMeta && (
        <CreateRoleModal
          onClose={() => setIsNewModalOpened(false)}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          rolesMeta={rolesMeta}
          isActive={isNewModalOpened}
          isReadonlyPermission={isReadonlyPermission}
        />
      )}
    </>
  );
};

export default RolePage;
